import React, { createContext, useContext } from "react";
import type { IconType } from "react-icons";
import { FiArrowRight, FiCheckCircle, FiChevronRight } from "react-icons/fi";

/**
 * Building blocks for the Admin Guide. Plain tables, notes and code come from the
 * Knowledge Center (`ApiDocs/primitives`); these add steps, tab badges, flows and
 * the boxes the diagrams are drawn with, in the same light look.
 */

export type AdminGuideSectionId =
  | "start-here"
  | "how-access-works"
  | "admin-pages"
  | "onboarding-client"
  | "onboarding-company"
  | "onboarding-asset-group"
  | "onboarding-user"
  | "onboarding-an-asset"
  | "method-access"
  | "filecenter-folders"
  | "outage-assets"
  | "mobile-users"
  | "when-changes-apply"
  | "offboarding"
  | "troubleshooting";

/** Opens another section. The active section lives in AdminGuide. */
export const GuideNavContext = createContext<(id: AdminGuideSectionId) => void>(() => {});

/** An inline link to another section of the guide. */
export const GuideLink: React.FC<{ to: AdminGuideSectionId; children: React.ReactNode }> = ({
  to,
  children,
}) => {
  const go = useContext(GuideNavContext);
  return (
    <button
      type="button"
      onClick={() => go(to)}
      className="inline p-0 text-left font-medium text-red-600 hover:underline"
    >
      {children}
    </button>
  );
};

/** A card that opens another section: used for the "most common jobs" list. */
export const GuideCard: React.FC<{
  to: AdminGuideSectionId;
  icon: IconType;
  title: string;
  children: React.ReactNode;
}> = ({ to, icon: Icon, title, children }) => {
  const go = useContext(GuideNavContext);
  return (
    <button
      type="button"
      onClick={() => go(to)}
      className="group flex w-full items-start gap-3 rounded-lg border border-gray-200 bg-white p-4 text-left transition hover:border-red-200 hover:bg-red-50"
    >
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-red-50 text-red-600 group-hover:bg-white">
        <Icon className="h-4 w-4" aria-hidden="true" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block font-medium leading-6 text-gray-800">{title}</span>
        <span className="block text-xs leading-5 text-gray-500">{children}</span>
      </span>
      <FiArrowRight className="mt-1.5 shrink-0 text-gray-400 group-hover:text-red-600" aria-hidden="true" />
    </button>
  );
};

/** The one line that opens each section: when you would use it. */
export const Lead: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="text-[15px] leading-7 text-gray-800">{children}</p>
);

export const List: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <ul className="list-disc space-y-2 pl-5">{children}</ul>
);

export const NumberedList: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <ol className="list-decimal space-y-2 pl-5">{children}</ol>
);

/** "Entitlements > Role": where a step happens, styled like the admin tab bar. */
export const TabBadge: React.FC<{ tab: string }> = ({ tab }) => {
  const parts = tab.split(">").map((p) => p.trim());
  return (
    <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-md bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600 ring-1 ring-inset ring-gray-200">
      {parts.map((part, i) => (
        <React.Fragment key={part}>
          {i > 0 && <FiChevronRight className="h-3 w-3 text-gray-400" aria-label="then" />}
          <span className={i === parts.length - 1 ? "text-gray-800" : undefined}>{part}</span>
        </React.Fragment>
      ))}
    </span>
  );
};

interface StepProps {
  title: string;
  /** Where the step happens, e.g. "Entitlements > Role". */
  tab?: string;
  /** How to tell the step worked. */
  check?: React.ReactNode;
  children?: React.ReactNode;
  /** Set by Steps. */
  n?: number;
}

/** One numbered step card. Use inside Steps, which numbers them. */
export const Step: React.FC<StepProps> = ({ title, tab, check, children, n }) => (
  <li className="relative flex gap-4">
    <span className="relative z-10 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-red-600 text-xs font-semibold text-white ring-4 ring-white">
      {n}
    </span>
    <div className="min-w-0 flex-1 rounded-lg border border-gray-200 bg-white px-4 py-3">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <h4 className="font-semibold leading-7 text-gray-800">{title}</h4>
        {tab && <TabBadge tab={tab} />}
      </div>
      {children && <div className="mt-1 space-y-2">{children}</div>}
      {check && (
        <div className="mt-3 flex gap-2 border-t border-gray-100 pt-2 text-gray-600">
          <FiCheckCircle className="mt-1.5 shrink-0 text-green-600" aria-hidden="true" />
          <span>
            <span className="font-medium text-gray-700">Check: </span>
            {check}
          </span>
        </div>
      )}
    </div>
  </li>
);

/** Numbered step cards joined by a line on the left. */
export const Steps: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const items = React.Children.toArray(children).filter(React.isValidElement);
  return (
    <ol className="relative space-y-4 before:absolute before:bottom-6 before:left-[13px] before:top-6 before:w-0.5 before:bg-gray-200 before:content-['']">
      {items.map((child, i) => React.cloneElement(child as React.ReactElement<StepProps>, { n: i + 1 }))}
    </ol>
  );
};

/** The closing box of an onboarding section. */
export const CheckItWorked: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <section className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-green-900">
    <h3 className="mb-2 flex items-center gap-2 text-base font-semibold">
      <FiCheckCircle aria-hidden="true" />
      Check it worked
    </h3>
    <ul className="list-disc space-y-1.5 pl-5">{children}</ul>
  </section>
);

type Tone = "people" | "endpoint" | "asset" | "company" | "neutral";

const TONE: Record<Tone, { box: string; icon: string }> = {
  people: { box: "border-red-200", icon: "bg-red-50 text-red-600" },
  endpoint: { box: "border-blue-200", icon: "bg-blue-50 text-blue-600" },
  asset: { box: "border-green-200", icon: "bg-green-50 text-green-700" },
  company: { box: "border-dashed border-gray-300", icon: "bg-gray-100 text-gray-500" },
  neutral: { box: "border-gray-200", icon: "bg-gray-100 text-gray-600" },
};

/** A box in a diagram: icon, name, and the tab where you manage it. */
export const Node: React.FC<{
  title: string;
  caption?: React.ReactNode;
  icon?: IconType;
  tone?: Tone;
  className?: string;
  children?: React.ReactNode;
}> = ({ title, caption, icon: Icon, tone = "neutral", className = "", children }) => (
  <div className={`rounded-lg border bg-white px-3 py-2.5 shadow-sm ${TONE[tone].box} ${className}`}>
    <div className="flex items-center gap-2.5">
      {Icon && (
        // Icons only where the diagram has room for them.
        <span className={`hidden h-7 w-7 shrink-0 items-center justify-center rounded-full 2xl:flex ${TONE[tone].icon}`}>
          <Icon className="h-3.5 w-3.5" aria-hidden="true" />
        </span>
      )}
      <div className="min-w-0">
        <div className="text-sm font-semibold leading-5 text-gray-800">{title}</div>
        {caption && <div className="text-xs leading-4 text-gray-500">{caption}</div>}
      </div>
    </div>
    {children && <div className="mt-2 space-y-1.5">{children}</div>}
  </div>
);

/** A small line inside a Node: a name (often code) and what it is. */
export const NodeItem: React.FC<{ name: React.ReactNode; children?: React.ReactNode }> = ({
  name,
  children,
}) => (
  <div className="rounded-md bg-gray-50 px-2 py-1 text-xs leading-4 text-gray-600">
    <span className="font-medium text-gray-800">{name}</span>
    {children && <span className="block">{children}</span>}
  </div>
);

/** A right-pointing arrow that fills its grid cell, with a short label above the line. */
export const Arrow: React.FC<{ label?: string }> = ({ label }) => (
  <div className="relative flex h-full min-h-[44px] items-center px-1">
    {label && (
      <span className="absolute inset-x-0 bottom-1/2 mb-1.5 px-1 text-center text-[11px] leading-tight text-gray-500">
        {label}
      </span>
    )}
    <span className="h-0.5 flex-1 bg-gray-300" />
    <svg width="8" height="10" viewBox="0 0 8 10" className="shrink-0 text-gray-400" aria-hidden="true">
      <path d="M0 0L8 5L0 10Z" fill="currentColor" />
    </svg>
  </div>
);

/** A diagram frame: scrolls sideways on narrow screens instead of squeezing the text. */
export const Diagram: React.FC<{ label: string; caption?: React.ReactNode; minWidth: number; children: React.ReactNode }> = ({
  label,
  caption,
  minWidth,
  children,
}) => (
  <figure aria-label={label} className="rounded-lg border border-gray-200 bg-gray-50">
    <div className="overflow-x-auto p-4">
      <div style={{ minWidth }}>{children}</div>
    </div>
    {caption && (
      <figcaption className="border-t border-gray-200 px-4 py-2 text-xs leading-5 text-gray-500">{caption}</figcaption>
    )}
  </figure>
);

export interface FlowStep {
  title: string;
  /** Small text above the title, e.g. a time ("Every hour at :06") or a step number. */
  kicker?: string;
  detail?: React.ReactNode;
  tone?: "default" | "accent" | "muted";
}

const FLOW_TONE: Record<NonNullable<FlowStep["tone"]>, string> = {
  default: "border-gray-200 bg-white",
  accent: "border-red-200 bg-red-50",
  muted: "border-dashed border-gray-300 bg-white",
};

/**
 * A left-to-right sequence of equal cards joined by arrows: a process or a timeline.
 * Below the lg breakpoint it stacks, with the arrows pointing down.
 */
export const Flow: React.FC<{ label: string; steps: FlowStep[] }> = ({ label, steps }) => (
  <ol aria-label={label} className="flex flex-col gap-6 lg:flex-row lg:items-stretch">
    {steps.map((step, i) => (
      <li key={step.title} className="relative flex lg:min-w-0 lg:flex-1">
        <div className={`flex-1 rounded-lg border px-3 py-2.5 ${FLOW_TONE[step.tone ?? "default"]}`}>
          {step.kicker && (
            <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">{step.kicker}</div>
          )}
          <div className="text-sm font-semibold leading-6 text-gray-800">{step.title}</div>
          {step.detail && <div className="text-xs leading-5 text-gray-600">{step.detail}</div>}
        </div>
        {i < steps.length - 1 && (
          // Centred in the 24px gap after the card.
          <span
            className="absolute -bottom-5 left-1/2 -translate-x-1/2 text-gray-400 lg:-right-[19px] lg:bottom-auto lg:left-auto lg:top-1/2 lg:-translate-y-1/2 lg:translate-x-0"
            aria-hidden="true"
          >
            <FiArrowRight className="h-3.5 w-3.5 rotate-90 lg:rotate-0" />
          </span>
        )}
      </li>
    ))}
  </ol>
);

/** Keeps a name such as `<client>-read-all` on one line. */
export const NoWrap: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="whitespace-nowrap">{children}</span>
);

/** The Onboarding checklist's status pill, so the guide matches what admins see. */
export const StatusPill: React.FC<{ status: "done" | "missing" | "warn" | "choice" }> = ({ status }) => {
  const style = {
    done: "bg-green-100 text-green-700",
    missing: "bg-red-100 text-red-700",
    warn: "bg-amber-100 text-amber-700",
    choice: "bg-gray-100 text-gray-700",
  }[status];
  const text = { done: "Done", missing: "Missing", warn: "Check", choice: "Choice" }[status];
  return (
    <span className={`inline-flex w-20 shrink-0 justify-center rounded-full px-2 py-1 text-xs font-semibold ${style}`}>
      {text}
    </span>
  );
};

/** An Access Explorer method cell: met, unmet, or the default when nothing is stored. */
export const MethodCell: React.FC<{ state: "met" | "unmet" | "default"; level: string }> = ({ state, level }) => {
  if (state === "met") {
    return (
      <span className="whitespace-nowrap text-gray-700">
        <span className="font-bold text-green-600">✓</span> {level}
      </span>
    );
  }
  if (state === "unmet") {
    return (
      <span className="whitespace-nowrap text-gray-400">
        <span className="font-bold text-red-600">✕</span> {level}
      </span>
    );
  }
  return <span className="whitespace-nowrap italic text-gray-400">{level}</span>;
};
